import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, money, SMS_ENABLED } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { NotesCard, SatelliteView } from "@/components/DetailBits";
import DocumentDialog from "@/components/DocumentDialog";
import ClientDialog from "@/components/ClientDialog";
import SmsDialog from "@/components/SmsDialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, EnvelopeSimple, Phone, MapPin, Buildings, FileText, Briefcase, Receipt, ChatText, Plus, Images, FolderOpen, LinkSimple, ArrowSquareOut, PencilSimple, UsersThree, UserCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [activity, setActivity] = useState({ quotes: [], jobs: [], invoices: [] });
  const [photoFolders, setPhotoFolders] = useState([]);
  const [docType, setDocType] = useState(null); // "quote" | "job" | "invoice"
  const [smsOpen, setSmsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const load = () => {
    api.getClient(id).then(setClient).catch(() => toast.error("Client not found"));
    api.clientActivity(id).then(setActivity).catch(() => {});
    api.clientPhotos(id).then(setPhotoFolders).catch(() => {});
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const copyPortalLink = async () => {
    try {
      const { url } = await api.portalLink(id);
      await navigator.clipboard.writeText(url);
      toast.success("Portal link copied to clipboard");
    } catch (e) {
      toast.error("Could not get portal link");
    }
  };

  if (!client) return <div className="p-10 text-slate-400">Loading client…</div>;

  const totalBilled = activity.invoices.filter((i) => i.status === "paid").reduce((s, i) => s + i.total, 0);

  return (
    <div className="p-8 max-w-[1200px]">
      <button onClick={() => navigate("/clients")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4 transition-colors duration-200" data-testid="back-to-clients"><ArrowLeft size={16} /> Back to Clients</button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">{client.name}</h1>
          {client.company && <p className="text-slate-500 mt-0.5">{client.company}</p>}
          <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-600">
            {client.email && <span className="inline-flex items-center gap-1.5"><EnvelopeSimple size={14} className="text-[#00B4D8]" />{client.email}</span>}
            {client.phone && <span className="inline-flex items-center gap-1.5"><Phone size={14} className="text-[#00B4D8]" />{client.phone}</span>}
          </div>
        </div>
        <div className="bg-[#0B1726] rounded-lg px-6 py-4 text-white text-right">
          <div className="text-[11px] uppercase tracking-widest text-slate-400">Lifetime Paid</div>
          <div className="font-display text-2xl font-extrabold text-[#00B4D8]">{money(totalBilled)}</div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Button onClick={() => setEditOpen(true)} variant="outline" data-testid="edit-client-button"><PencilSimple size={16} className="mr-1.5 text-[#00B4D8]" /> Edit Client</Button>
        <Button onClick={() => setDocType("quote")} className="bg-[#0B1726] hover:bg-slate-800" data-testid="client-new-quote"><Plus size={16} className="mr-1" /> New Quote</Button>
        <Button onClick={() => setDocType("job")} variant="outline" data-testid="client-new-job"><Briefcase size={16} className="mr-1.5" /> New Job</Button>
        <Button onClick={() => setDocType("invoice")} variant="outline" data-testid="client-new-invoice"><Receipt size={16} className="mr-1.5" /> New Invoice</Button>
        {SMS_ENABLED && <Button onClick={() => setSmsOpen(true)} variant="outline" data-testid="client-text"><ChatText size={16} className="mr-1.5 text-[#00B4D8]" /> Text</Button>}
        <Button onClick={copyPortalLink} variant="outline" data-testid="client-portal-link"><LinkSimple size={16} className="mr-1.5 text-[#00B4D8]" /> Copy Portal Link</Button>
      </div>

      {/* Properties */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3"><Buildings size={16} weight="duotone" className="text-[#00B4D8]" /><h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Properties</h3></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {(client.properties || []).length === 0 && <p className="text-slate-400 text-sm">No properties on file.</p>}
          {(client.properties || []).map((p) => (
            <div key={p.id} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm" data-testid={`property-${p.id}`}>
              <div className="flex items-start gap-2"><MapPin size={16} className="text-[#00B4D8] mt-0.5 shrink-0" /><div><a href={`https://www.google.com/maps?q=${encodeURIComponent([p.address, p.city, p.state, p.zip].filter(Boolean).join(", "))}&t=k&z=19`} target="_blank" rel="noreferrer" className="font-medium text-slate-800 text-sm hover:text-[#00B4D8] hover:underline inline-flex items-center gap-1 transition-colors duration-200" data-testid={`property-maps-link-${p.id}`}>{p.address}<ArrowSquareOut size={12} className="opacity-60" /></a><div className="text-xs text-slate-500">{[p.city, p.state, p.zip].filter(Boolean).join(", ")}</div></div></div>
              <SatelliteView address={[p.address, p.city, p.state, p.zip].filter(Boolean).join(", ")} />
            </div>
          ))}
        </div>
      </div>

      {/* Additional contacts / sub-clients */}
      {(client.contacts || []).length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3"><UsersThree size={16} weight="duotone" className="text-[#00B4D8]" /><h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Additional Contacts</h3></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {client.contacts.map((c) => (
              <div key={c.id} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm" data-testid={`contact-${c.id}`}>
                <div className="flex items-center gap-2">
                  <UserCircle size={28} weight="duotone" className="text-slate-400 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-800 text-sm truncate">{c.name}</div>
                    {c.title && <div className="text-[11px] uppercase tracking-wider text-slate-400">{c.title}</div>}
                  </div>
                </div>
                <div className="mt-2 space-y-1 text-sm text-slate-600">
                  {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 hover:text-[#00B4D8] transition-colors"><EnvelopeSimple size={13} className="text-slate-400" />{c.email}</a>}
                  {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 hover:text-[#00B4D8] transition-colors"><Phone size={13} className="text-slate-400" />{c.phone}</a>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Tabs defaultValue="jobs">
            <TabsList className="mb-4">
              <TabsTrigger value="jobs" data-testid="tab-jobs"><Briefcase size={15} className="mr-1.5" /> Jobs ({activity.jobs.length})</TabsTrigger>
              <TabsTrigger value="quotes" data-testid="tab-quotes"><FileText size={15} className="mr-1.5" /> Quotes ({activity.quotes.length})</TabsTrigger>
              <TabsTrigger value="invoices" data-testid="tab-invoices"><Receipt size={15} className="mr-1.5" /> Invoices ({activity.invoices.length})</TabsTrigger>
              <TabsTrigger value="photos" data-testid="tab-photos"><Images size={15} className="mr-1.5" /> Photos ({photoFolders.reduce((s, f) => s + f.count, 0)})</TabsTrigger>
            </TabsList>
            <TabsContent value="jobs"><ActivityTable rows={activity.jobs} type="jobs" nav={navigate} labelCol="Title" /></TabsContent>
            <TabsContent value="quotes"><ActivityTable rows={activity.quotes} type="quotes" nav={navigate} /></TabsContent>
            <TabsContent value="invoices"><ActivityTable rows={activity.invoices} type="invoices" nav={navigate} /></TabsContent>
            <TabsContent value="photos"><PhotoFolders folders={photoFolders} nav={navigate} /></TabsContent>
          </Tabs>
        </div>
        <NotesCard value={client.notes} onSave={(t) => api.patchClientNotes(id, t)} title="Client Notes" />
      </div>

      <DocumentDialog open={!!docType} onClose={() => setDocType(null)} type={docType || "quote"} presetClientId={id} onSaved={load} />
      <ClientDialog open={editOpen} onClose={() => setEditOpen(false)} editing={client} onSaved={load} />
      {SMS_ENABLED && <SmsDialog open={smsOpen} onClose={() => setSmsOpen(false)} clientId={id} clientName={client.name} defaultPhone={client.phone || ""} />}
    </div>
  );
}

function PhotoFolders({ folders, nav }) {
  const [openId, setOpenId] = useState(folders[0]?.job_id || null);
  if (!folders.length) return <div className="bg-white border border-slate-200 rounded-lg p-8 text-center text-slate-400 text-sm">No photos yet. Upload photos from any job and they'll be filed here by job.</div>;
  return (
    <div className="space-y-3">
      {folders.map((f) => (
        <div key={f.job_id} className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm" data-testid={`photo-folder-${f.job_id}`}>
          <button onClick={() => setOpenId(openId === f.job_id ? null : f.job_id)} className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors duration-200">
            <FolderOpen size={20} weight="duotone" className="text-[#00B4D8]" />
            <div className="flex-1 text-left">
              <div className="font-semibold text-slate-900 text-sm">{f.job_number} · {f.job_title}</div>
              <div className="text-xs text-slate-500">{f.count} photo{f.count !== 1 ? "s" : ""}</div>
            </div>
            <span className="text-xs text-[#00B4D8] font-semibold hover:underline" onClick={(e) => { e.stopPropagation(); nav(`/jobs/${f.job_id}`); }}>Open job</span>
          </button>
          {openId === f.job_id && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 p-3 border-t border-slate-100">
              {f.photos.map((p) => (
                <a key={p.id} href={api.photoUrl(p.url)} target="_blank" rel="noreferrer" className="aspect-square rounded-md overflow-hidden bg-slate-100 block" data-testid={`client-photo-${p.id}`}>
                  <img src={api.photoUrl(p.url)} alt={p.original_filename} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" loading="lazy" />
                </a>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function ActivityTable({ rows, type, nav, labelCol }) {
  if (!rows.length) return <div className="bg-white border border-slate-200 rounded-lg p-8 text-center text-slate-400 text-sm">Nothing here yet.</div>;
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
      <Table>
        <TableHeader><TableRow className="bg-slate-50"><TableHead>Number</TableHead><TableHead>{labelCol || "Status"}</TableHead>{labelCol && <TableHead>Status</TableHead>}<TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => nav(type === "invoices" ? "/invoices" : `/${type}/${r.id}`)} data-testid={`activity-${type}-${r.id}`}>
              <TableCell className="font-mono font-semibold text-[#0B1726]">{r.number}</TableCell>
              {labelCol && <TableCell>{r.title}</TableCell>}
              <TableCell><StatusBadge status={r.status} /></TableCell>
              <TableCell className="text-right font-semibold">{money(r.total)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
